<template>
  <div class="dashboard-outer">
  <div class="dashboard">

    <novedades-banner contexto="pos" titulo="Cambió la pantalla de caja" subtitulo="Nada que aprender de nuevo, sólo se ve distinto. Lo importante:" />

    <!-- ── HERO ── -->
    <div class="dashboard-hero">
      <!-- Columna izquierda -->
      <div class="dashboard-hero-left">
        <div class="dashboard-greeting">
          <div class="dashboard-greeting-text">{{ saludo }}, {{ nombreCorto }}</div>
          <div class="dashboard-sucursal" v-if="sucursal">
            <v-icon x-small color="grey darken-1" class="mr-1">mdi-store-outline</v-icon>
            {{ sucursal.nombre }}
          </div>
        </div>

        <!-- Sin caja -->
        <div v-if="!arqueoCaja" class="dashboard-nocaja">
          <div class="dashboard-nocaja-icon">
            <v-icon size="48" color="grey darken-1">mdi-cash-register</v-icon>
          </div>
          <div class="dashboard-nocaja-title">Para vender, iniciá una caja</div>
          <div class="dashboard-nocaja-sub">Seleccioná una caja y abrí un arqueo para comenzar a registrar ventas.</div>
          <v-btn color="primary" depressed large :to="{ name: 'pos.cajas' }" class="mt-6">
            <v-icon left>mdi-cash-register</v-icon>
            Iniciar caja
          </v-btn>
        </div>

        <!-- Con caja -->
        <div v-else>
          <div class="dashboard-caja-activa">
            <v-icon x-small color="success" class="mr-1">mdi-circle</v-icon>
            <span class="dashboard-caja-activa-label">Caja operativa:</span>
            <span class="dashboard-caja-activa-nombre">{{ caja ? caja.nombre : '' }}</span>
          </div>
          <div class="dashboard-actions">
            <v-btn color="primary" depressed large :to="{ name: 'pos.venta.registrar' }">
              <v-icon left>mdi-cart-outline</v-icon>
              Nueva venta
              <span class="dashboard-shortcut">F1</span>
            </v-btn>
            <v-btn
              v-if="configuracion && configuracion.ventaConPedido"
              outlined color="primary" large
              :to="{ name: 'pos.venta.pedidos.nuevo' }"
            >
              <v-icon left>mdi-clipboard-text-clock-outline</v-icon>
              Nuevo pedido
              <span class="dashboard-shortcut">F2</span>
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Columna derecha: fecha y hora -->
      <div class="dashboard-hero-right">
        <div class="dashboard-fecha">{{ fechaHoy }}</div>
        <div class="dashboard-hora">{{ horaActual }}</div>
      </div>
    </div>

    <!-- ── ACCIONES PENDIENTES DE STOCK (encargado) ── -->
    <alertas-stock-panel class="dashboard-section" />

    <!-- ── ACCESOS DIRECTOS (solo con caja) ── -->
    <div v-if="arqueoCaja" class="dashboard-section">
      <div class="dashboard-section-title">Accesos rápidos</div>
      <div class="dashboard-shortcuts-grid">
        <v-card
          v-for="item in accesosRapidos"
          :key="item.route_name"
          flat
          class="dashboard-shortcut-card"
          :to="{ name: item.route_name }"
        >
          <v-icon size="28" color="grey lighten-1" class="mb-3">{{ item.icon }}</v-icon>
          <div class="dashboard-shortcut-label">{{ item.name }}</div>
        </v-card>
      </div>
    </div>

    <!-- ── ACCESOS POR PERMISO ── -->
    <div v-if="accesosPorPermiso.length" class="dashboard-section">
      <div class="dashboard-section-title">Administración</div>
      <div class="dashboard-shortcuts-grid">
        <v-card
          v-for="item in accesosPorPermiso"
          :key="item.route_name"
          flat
          class="dashboard-shortcut-card"
          :to="{ name: item.route_name }"
        >
          <v-icon size="28" color="grey lighten-1" class="mb-3">{{ item.icon }}</v-icon>
          <div class="dashboard-shortcut-label">{{ item.name }}</div>
        </v-card>
      </div>
    </div>

  </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import AlertasStockPanel from "@/components/inventario/AlertasStockPanel.vue";
import NovedadesBanner from "@/components/NovedadesBanner.vue";

export default {
  name: "PosDashboard",

  components: { AlertasStockPanel, NovedadesBanner },

  props: {
    menuItems: { type: Array, default: () => [] },
  },

  data() {
    return {
      horaActual: "",
      _horaTimer: null,
    };
  },

  computed: {
    ...mapState("auth", ["user"]),
    ...mapState("sucursales", ["sucursal", "caja", "arqueoCaja", "configuracion"]),
    ...mapGetters("auth", ["tienePermiso"]),

    nombreCorto() {
      const nombre = this.user?.full_name || this.user?.username || "";
      return nombre.split(" ")[0];
    },

    saludo() {
      const h = new Date().getHours();
      if (h < 12) return "Buenos días";
      if (h < 19) return "Buenas tardes";
      return "Buenas noches";
    },

    fechaHoy() {
      return new Date().toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" });
    },

    accesosRapidos() {
      return [
        { icon: "mdi-cart-outline", name: "Ventas", route_name: "pos.venta" },
        { icon: "mdi-package-variant-closed", name: "Productos", route_name: "pos.productos.home" },
        { icon: "mdi-cart-arrow-down", name: "Compras", route_name: "pos.compras" },
        { icon: "mdi-file-document-edit-outline", name: "Gastos", route_name: "pos.egresos.gastos" },
        { icon: "mdi-account-group-outline", name: "Clientes", route_name: "pos.clientes" },
        { icon: "mdi-truck-delivery-outline", name: "Proveedores", route_name: "pos.proveedores" },
        { icon: "mdi-cash-multiple", name: "Movimientos", route_name: "pos.movimientos" },
        { icon: "mdi-tag-search-outline", name: "Consulta de precios", route_name: "pos.consultaPrecios" },
      ];
    },

    accesosPorPermiso() {
      const items = [];
      if (this.tienePermiso("realizar_inventario")) {
        items.push({ icon: "mdi-clipboard-list-outline", name: "Inventarios", route_name: "pos.inventarios" });
      }
      if (this.tienePermiso("crear_producto")) {
        items.push({ icon: "mdi-store-outline", name: "Productos de la empresa", route_name: "empresa.productosLayout" });
      }
      return items;
    },
  },

  methods: {},

  created() {
    this.horaActual = new Date().toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
    this._horaTimer = setInterval(() => {
      this.horaActual = new Date().toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
    }, 30000);
  },

  beforeDestroy() {
    clearInterval(this._horaTimer);
  },
};
</script>

<style scoped>
/* ── Outer: fondo de página ── */
.dashboard-outer {
  min-height: 100%;
  background: #141414;
  padding: 32px 16px 48px;
}

/* ── Inner: ancho centrado como ML ── */
.dashboard {
  max-width: 1280px;
  margin: 0 auto;
}

/* ── Hero ── */
.dashboard-hero {
  background: #1a1a1a;
  border: 1px solid #222;
  border-radius: 8px;
  padding: 40px 40px 36px;
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}

.dashboard-hero-left {
  flex: 1;
  min-width: 0;
}

.dashboard-hero-right {
  flex-shrink: 0;
  text-align: right;
  padding-top: 4px;
}

.dashboard-fecha {
  font-size: 0.85rem;
  color: #666;
  text-transform: capitalize;
}

.dashboard-hora {
  font-size: 2.4rem;
  font-weight: 700;
  color: #333;
  line-height: 1;
  margin-top: 4px;
  font-variant-numeric: tabular-nums;
}

.dashboard-greeting-text {
  font-size: 1.6rem;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
}

.dashboard-sucursal {
  font-size: 0.82rem;
  color: #777;
  margin-top: 6px;
}

/* Sin caja */
.dashboard-nocaja {
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.dashboard-nocaja-icon {
  margin-bottom: 12px;
}

.dashboard-nocaja-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #cccccc;
}

.dashboard-nocaja-sub {
  font-size: 0.85rem;
  color: #666;
  margin-top: 6px;
  max-width: 420px;
  line-height: 1.5;
}

/* Caja activa */
.dashboard-caja-activa {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 20px;
  margin-bottom: 16px;
  font-size: 0.82rem;
}

.dashboard-caja-activa-label {
  color: #666;
}

.dashboard-caja-activa-nombre {
  color: #cccccc;
  font-weight: 600;
}

/* Con caja */
.dashboard-actions {
  margin-top: 32px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.dashboard-shortcut {
  margin-left: 10px;
  font-size: 0.68rem;
  font-family: monospace;
  background: rgba(255,255,255,0.1);
  padding: 2px 5px;
  border-radius: 4px;
  font-weight: 800;
  letter-spacing: 0.5px;
}

/* ── Secciones ── */
.dashboard-section {
  background: #1a1a1a;
  border: 1px solid #222;
  border-radius: 8px;
  padding: 28px 32px;
  margin-bottom: 16px;
}

.dashboard-section-title {
  font-size: 0.75rem;
  font-weight: 700;
  color: #666;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 16px;
}

/* ── Accesos rápidos ── */
.dashboard-shortcuts-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

@media (max-width: 1100px) {
  .dashboard-shortcuts-grid { grid-template-columns: repeat(4, 1fr); }
}
@media (max-width: 720px) {
  .dashboard-shortcuts-grid { grid-template-columns: repeat(3, 1fr); }
  .dashboard-hero { padding: 28px 20px 24px; }
  .dashboard-section { padding: 20px 16px; }
  .dashboard-outer { padding: 16px 12px 32px; }
}

.dashboard-shortcut-card {
  background: #1e1e1e !important;
  border: 1px solid #2a2a2a !important;
  border-radius: 10px !important;
  padding: 20px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;
  text-decoration: none;
}

.dashboard-shortcut-card:hover {
  background: #252525 !important;
  border-color: #3a3a3a !important;
}

.dashboard-shortcut-label {
  font-size: 0.78rem;
  color: #aaaaaa;
  text-align: center;
  line-height: 1.3;
}
</style>
